// Helper murni untuk Broadcast V2 multi sender & monitoring jadwal.
// Tidak bergantung pada DOM agar mudah diuji dan dipakai ulang oleh wizard,
// daftar broadcast, dan detail broadcast.

export const BROADCAST_STATUS_LABELS = {
  draft: "Draft",
  scheduled: "Terjadwal",
  waiting_start: "Menunggu diproses",
  waiting_schedule: "Menunggu jadwal",
  sending: "Sedang mengirim",
  queue_delayed: "Antrean terlambat",
  completed: "Selesai",
  partial: "Berhasil sebagian",
  failed: "Gagal",
  paused: "Dijeda",
  cancelled: "Dibatalkan"
};

// Preset mengikuti backend (broadcastSpeedModels.go). Mode safe memakai
// istirahat acak 30-120 menit sehingga estimasi ditampilkan sebagai rentang.
export const BROADCAST_SPEED_PRESETS = {
  safe: {
    waitMin: 1,
    waitMax: 99,
    stopAfter: 15,
    restMin: 1800,
    restMax: 7200
  },
  normal: {
    waitMin: 1,
    waitMax: 99,
    stopAfter: 50,
    restMin: 1200,
    restMax: 1200
  },
  fast: {
    waitMin: 1,
    waitMax: 99,
    stopAfter: 100,
    restMin: 600,
    restMax: 600
  }
};

export function normalizeLicenseIds(raw, fallback) {
  let list = [];
  if (Array.isArray(raw)) {
    list = raw.slice();
  } else if (typeof raw === "string" && raw.trim()) {
    const value = raw.trim();
    if (value.charAt(0) === "[") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) list = parsed;
      } catch (error) {
        list = [];
      }
    } else {
      list = value.split(",");
    }
  }

  if (!list.length && fallback) {
    list = [fallback];
  }

  const seen = {};
  const result = [];
  list.forEach(item => {
    const id = String(item === null || typeof item === "undefined" ? "" : item).trim();
    if (!id || seen[id]) return;
    seen[id] = true;
    result.push(id);
  });
  if (!result.length && fallback) {
    const id = String(fallback).trim();
    if (id) result.push(id);
  }
  return result;
}

export function isSenderSelectable(sender) {
  if (!sender || !sender.id) return false;
  if (Number(sender.is_online_status) !== 1) return false;
  if (
    typeof sender.multi_device_jid !== "undefined" &&
    sender.multi_device_jid !== null &&
    String(sender.multi_device_jid).trim() === ""
  ) {
    return false;
  }
  return true;
}

export function senderStatusLabel(sender) {
  if (!sender) return "Tidak diketahui";
  return Number(sender.is_online_status) === 1
    ? "Tersambung"
    : "Tidak Tersambung";
}

export function selectedSenderOptions(options, selectedIds) {
  const source = Array.isArray(options) ? options : [];
  const ids = Array.isArray(selectedIds) ? selectedIds : [];
  const map = {};
  source.forEach(sender => {
    if (sender && sender.id !== undefined) map[String(sender.id)] = sender;
  });
  return ids.map(id => map[String(id)]).filter(Boolean);
}

export function senderSummaryLabel(senders, maxNames) {
  const list = Array.isArray(senders) ? senders.filter(Boolean) : [];
  if (!list.length) return "Belum dipilih";
  if (list.length === 1) {
    return (
      (list[0].license_name || "Nomor WhatsApp") +
      " · " +
      (list[0].license_phone_number || "")
    ).trim();
  }
  const limit = Number(maxNames) > 0 ? Number(maxNames) : 2;
  const names = list
    .slice(0, limit)
    .map(sender => sender.license_name || "Nomor WhatsApp")
    .join(", ");
  const suffix = list.length > limit ? " +" + (list.length - limit) : "";
  return list.length + " nomor · " + names + suffix;
}

export function timezoneLabel(date) {
  const value = date instanceof Date ? date : new Date();
  const offset = -value.getTimezoneOffset() / 60;
  if (offset === 7) return "WIB";
  if (offset === 8) return "WITA";
  if (offset === 9) return "WIT";
  const sign = offset >= 0 ? "+" : "";
  return "UTC" + sign + offset;
}

export function formatClock(unixSeconds, withZone) {
  const value = Number(unixSeconds);
  if (!Number.isFinite(value) || value <= 0) return "-";
  const date = new Date(value * 1000);
  const pad = number => String(number).padStart(2, "0");
  const clock =
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes()) +
    ":" +
    pad(date.getSeconds());
  return withZone === false ? clock : clock + " " + timezoneLabel(date);
}

export function formatDateTime(unixSeconds) {
  const value = Number(unixSeconds);
  if (!Number.isFinite(value) || value <= 0) return "-";
  const date = new Date(value * 1000);
  const pad = number => String(number).padStart(2, "0");
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  return (
    day +
    "/" +
    month +
    "/" +
    year +
    " " +
    formatClock(value)
  );
}

export function formatCountdown(totalSeconds) {
  const value = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const seconds = value % 60;
  const pad = number => String(number).padStart(2, "0");
  return pad(hours) + ":" + pad(minutes) + ":" + pad(seconds);
}

export function humanizeDuration(totalSeconds) {
  const value = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  if (value < 60) return value + " detik";
  const minutes = Math.floor(value / 60);
  if (minutes < 60) {
    const seconds = value % 60;
    return seconds ? minutes + " menit " + seconds + " detik" : minutes + " menit";
  }
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes
    ? hours + " jam " + restMinutes + " menit"
    : hours + " jam";
}

export function statusLabel(status) {
  return BROADCAST_STATUS_LABELS[status] || "Menunggu";
}

export function statusTone(status) {
  if (status === "completed") return "is-success";
  if (status === "partial" || status === "failed") return "is-error";
  if (status === "draft" || status === "paused" || status === "cancelled") {
    return "is-muted";
  }
  if (status === "sending") return "is-sending";
  if (status === "queue_delayed") return "is-warning";
  return "is-waiting";
}

export function resolveSpeedConfig(speedMode, custom) {
  const source = custom || {};
  if (speedMode === "custom") {
    const waitMin = Math.max(0, Number(source.waitMin) || 0);
    let waitMax = Math.max(0, Number(source.waitMax) || 0);
    if (waitMax <= waitMin) waitMax = waitMin + 1;
    // Samakan default backend: StopAfter <= 0 menjadi 256 dan
    // RestFor <= 0 menjadi 5 detik.
    let stopAfter = Number(source.stopAfter) || 0;
    if (stopAfter <= 0) stopAfter = 256;
    let restFor = Number(source.restFor) || 0;
    if (restFor <= 0) restFor = 5;
    return {
      waitMin,
      waitMax,
      stopAfter,
      restMin: restFor,
      restMax: restFor
    };
  }
  return Object.assign({}, BROADCAST_SPEED_PRESETS[speedMode] || BROADCAST_SPEED_PRESETS.normal);
}

// estimateBroadcastTiming meniru calculateSpeedInSeconds:
// pesan pertama tidak diberi jeda, pesan berikutnya acak waitMin-waitMax,
// dan setelah stopAfter pesan sistem berhenti selama restFor.
export function estimateBroadcastTiming(options) {
  const source = options || {};
  const recipients = Math.max(0, Number(source.recipients) || 0);
  if (!recipients) return null;

  const config = resolveSpeedConfig(source.speedMode, source.custom);
  const stopAfter = Math.max(1, Number(config.stopAfter) || 1);
  let minSeconds = 0;
  let maxSeconds = 0;
  let loopMin = 0;
  let loopMax = 0;

  for (let index = 0; index < recipients; index += 1) {
    if (index > 0) {
      minSeconds += config.waitMin;
      maxSeconds += config.waitMax;
    }
    loopMin += 1;
    loopMax += 1;
    if (loopMin >= stopAfter) {
      minSeconds += config.restMin;
      loopMin = 0;
    }
    if (loopMax >= stopAfter) {
      maxSeconds += config.restMax;
      loopMax = 0;
    }
  }

  return {
    recipients,
    minSeconds,
    maxSeconds,
    waitMin: config.waitMin,
    waitMax: config.waitMax,
    stopAfter,
    restMin: config.restMin,
    restMax: config.restMax
  };
}

export function attachMonitoring(monitoring, receivedAtMs, serverTime) {
  if (!monitoring) return null;
  return Object.assign({}, monitoring, {
    __received_at: Number(receivedAtMs) || Date.now(),
    __server_time: Number(serverTime || monitoring.server_time || 0)
  });
}

// remainingSeconds memakai remaining_seconds dari backend lalu dikurangi
// waktu berjalan sejak response diterima. Ini menghindari jam browser yang
// berbeda dengan jam server.
export function remainingSeconds(monitoring, nowMs) {
  if (!monitoring) return 0;
  const base = Math.max(0, Number(monitoring.remaining_seconds) || 0);
  const receivedAt = Number(monitoring.__received_at) || 0;
  if (receivedAt > 0) {
    const elapsed = Math.max(
      0,
      Math.floor(((Number(nowMs) || Date.now()) - receivedAt) / 1000)
    );
    return Math.max(0, base - elapsed);
  }
  const next = Number(monitoring.next_scheduled_at) || 0;
  const serverTime = Number(monitoring.__server_time || monitoring.server_time) || 0;
  if (next > 0 && serverTime > 0) {
    return Math.max(0, next - serverTime);
  }
  return base;
}

export function monitoringCounts(monitoring) {
  const source = monitoring || {};
  return {
    total: Number(source.total) || 0,
    pending: Number(source.pending) || 0,
    processing: Number(source.processing) || 0,
    sent: Number(source.sent) || 0,
    failed: Number(source.failed) || 0
  };
}

export function monitoringSenderCount(monitoring, fallbackLicenseId) {
  if (monitoring && Array.isArray(monitoring.license_ids) && monitoring.license_ids.length) {
    return monitoring.license_ids.length;
  }
  if (monitoring && Number(monitoring.sender_count) > 0) {
    return Number(monitoring.sender_count);
  }
  return fallbackLicenseId ? 1 : 0;
}

export function rowLicenseIds(row) {
  const source = row || {};
  return normalizeLicenseIds(source.license_ids, source.license_id);
}

export function monitoringQueueMessage(monitoring) {
  if (!monitoring) return "";
  if (monitoring.stalled_processing) {
    const delay = Number(monitoring.queue_delay_seconds) || 0;
    return (
      "Pesan sudah diklaim " +
      humanizeDuration(delay) +
      " lalu dan belum selesai. Worker mungkin terlambat; pesan tidak dikirim ganda."
    );
  }
  if (monitoring.status === "queue_delayed") {
    const delay = Number(monitoring.queue_delay_seconds) || 0;
    return (
      "Jadwal pengiriman sudah lewat " +
      humanizeDuration(delay) +
      ". Worker sedang memproses antrean; pesan tidak terkirim ganda."
    );
  }
  if (monitoring.status === "waiting_schedule") {
    return "Pesan berikutnya sudah dijadwalkan. Proses tetap berjalan selama jadwal berikutnya belum tiba.";
  }
  return "";
}

export function buildTimeline(monitoring) {
  if (!monitoring) return [];
  const items = [];
  if (Number(monitoring.last_sent_at) > 0) {
    items.push({
      key: "last",
      tone: "is-success",
      time: formatClock(monitoring.last_sent_at),
      label: "Pesan terakhir berhasil dikirim."
    });
  }
  if (Number(monitoring.next_scheduled_at) > 0) {
    const isPast = Number(monitoring.next_scheduled_at) <= Number(monitoring.server_time || 0);
    items.push({
      key: "next",
      tone: isPast ? "is-warning" : "is-waiting",
      time: formatClock(monitoring.next_scheduled_at),
      label: isPast
        ? "Jadwal berikutnya sudah lewat dan sedang menunggu worker."
        : "Pesan berikutnya dijadwalkan."
    });
  }
  if (Number(monitoring.processing) > 0) {
    items.push({
      key: "processing",
      tone: "is-sending",
      time: "Sekarang",
      label: "Pesan sedang diproses oleh worker."
    });
  }
  if (Number(monitoring.failed) > 0) {
    items.push({
      key: "failed",
      tone: "is-error",
      time: "-",
      label: Number(monitoring.failed) + " pesan gagal dikirim."
    });
  }
  if (!items.length) {
    items.push({
      key: "empty",
      tone: "is-waiting",
      time: "-",
      label: "Jadwal pengiriman belum tersedia."
    });
  }
  return items;
}



// WEBPACK FOOTER //
// ./src/utils/broadcastMonitor.js